import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import MagicBackground from '../background/MagicBackground';
import DemoBeeBackground from '../background/demo/DemoBeeBackground';
import { levelDbService } from '../services/levelDbService';
import { gameAudio } from '../services/audioService';

const pulseKeyframes = `
  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7);
    }
    70% {
      transform: scale(1.05);
      box-shadow: 0 0 0 10px rgba(255, 193, 7, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
    }
  }
  
  @keyframes gameItemHover {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
    100% { transform: translateY(0px); }
  }
`;

interface GameStats {
  id: string;
  playerName?: string;
  totalAttempts: number;
  successfulAttempts: number;
  points?: any;
  isSinglePlay?: boolean;
}

export default function HomePage() {
  const [topGames, setTopGames] = useState<GameStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [isScrolled, setIsScrolled] = useState(true);
  const [isStartingGame, setIsStartingGame] = useState(false);
  const navigate = useNavigate();

  // UPDATED: Auto-generated game data with emoji rescue theme
  const autoGeneratedGame = {
    id: 'auto',
    name: 'Emoji Rescue Mission',
    description: 'Save captured emojis from alien UFOs across themed worlds!',
    icon: '🛸', // UFO icon for rescue theme
    isNew: true,
  };

  // Add handler for starting a new level game
  const handleStartLevelGame = async () => {
    try {
      setIsStartingGame(true);
      // Create a new level session
      const sessionId = await levelDbService.createLevelSession('Emoji Rescue Mission');
      // Navigate to the new session
      navigate(`/game/auto/${sessionId}`);
    } catch (error) {
      console.error('Error starting level game:', error);
      // Fallback to old auto mode if session creation fails
      navigate('/game/auto');
    } finally {
      setIsStartingGame(false);
    }
  };

  // Fetch top games from database
  useEffect(() => {
    const fetchTopGames = async () => {
      try {
        const q = query(
          collection(db, 'paths'),
          orderBy('totalAttempts', 'desc'),
          limit(20)
        );
        const querySnapshot = await getDocs(q);
        // Filter out isSinglePlay games
        const games = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter((game: any) => !game.isSinglePlay) as GameStats[];
        setTopGames(games);
      } catch (error) {
        console.error('Error fetching top games:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopGames();

    const handleScroll = () => {
      // setIsScrolled(window.scrollY > 50);
      setIsScrolled(true);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Initialize and play ready level audio on home page
    const initializeHomeAudio = async () => {
      try {
        await gameAudio.initialize();
        await gameAudio.playReadyLevelSound();
        console.log('Ready level audio started on home page');
      } catch (error) {
        console.error('Error initializing home audio:', error);
      }
    };

    initializeHomeAudio();

    // Cleanup audio when component unmounts
    return () => {
      gameAudio.stopGameStatusSounds();
    };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
      paddingBottom: '100px',
      textAlign: 'center',
      backgroundColor: '#f0f0f0'
    }}>
      <style>{pulseKeyframes}</style>
      <DemoBeeBackground />
      <MagicBackground />
      <h1 style={{
        fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
        marginBottom: '2rem',
        color: '#FFC107',
        lineHeight: 1.3,
        zIndex: 5,
        maxWidth: '800px',
        padding: '0 20px'
      }}>
        Ready to Rescue Cute Emojis? 🚀<br />
        <span style={{ fontSize: '0.7em', color: '#666' }}>
          Break UFOs • Save Cuteness • Pet Emojis
        </span>
      </h1>

      {/* Available Games Section (Always shown) */}
      <div style={{
        width: '100%',
        maxWidth: '600px',
        margin: '0 auto 40px auto',
        backgroundColor: 'rgba(0,0,0,0.8)',
        borderRadius: '15px',
        padding: '20px',
        zIndex: 5,
        border: '2px solid #333' // Subtle border for separation
      }}>
        <h2 style={{ 
          color: '#FFC107', 
          marginBottom: '1.5rem',
          fontSize: '1.8rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px'
        }}>
          Emoji Rescue Missions <span style={{ fontSize: '1.5em' }}>😍</span>
        </h2>
        {/* Operation Sindoor Game Item */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px',
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            borderRadius: '12px',
            color: '#FFC107',
            fontWeight: 'bold',
            border: '2px dashed #FFC107',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <span style={{ fontSize: '1.8rem', minWidth: '30px', color: '#FFD700' }}>{autoGeneratedGame.icon}</span>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '1.1rem', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{autoGeneratedGame.name}</span>
                {autoGeneratedGame.isNew && (
                  <span style={{
                    backgroundColor: '#E91E63',
                    color: '#fff',
                    padding: '2px 8px',
                    borderRadius: '5px',
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    animation: 'pulse 1.5s infinite'
                  }}>NEW</span>
                )}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.8, color: '#fff' }}>
                <span>{autoGeneratedGame.description}</span>
              </div>
            </div>
          </div>
          <button
            className="button-hover"
            style={{
              padding: '8px 20px',
              backgroundColor: '#FFC107',
              color: '#000',
              borderRadius: '25px',
              border: 'none',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              cursor: isStartingGame ? 'wait' : 'pointer',
              transition: 'transform 0.2s ease',
              opacity: isStartingGame ? 0.7 : 1
            }}
            onClick={handleStartLevelGame}
            disabled={isStartingGame}
          >
            <span>{isStartingGame ? '⌛' : '🎮'}</span> {isStartingGame ? 'Starting...' : 'Play'}
          </button>
        </div>
      </div>

      {/* Community Challenges Section (Conditionally shown/loaded) */}
      <div style={{
        width: '100%',
        maxWidth: '600px',
        margin: '0 auto',
        backgroundColor: 'rgba(0,0,0,0.8)',
        borderRadius: '15px',
        padding: '20px',
        zIndex: 5
      }}>
        <h2 style={{ 
          color: '#FFC107', 
          marginBottom: '1rem',
          fontSize: '1.5rem'
        }}>
          🏆 Community Challenges 🏆
        </h2>
        <p style={{
          color: '#FFC107',
          marginBottom: '1.5rem',
          fontSize: '1rem',
          opacity: 0.8
        }}>
          Top most challenges created by our community. Can you beat them? 🎯
        </p>

        {loading ? (
          <div style={{
            color: '#FFC107',
            fontSize: '1.2rem',
            textAlign: 'center',
            padding: '20px 0'
          }}>
            Loading community challenges...
            {/* Releasing soon... */}
          </div>
        ) : topGames.length > 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            {topGames.map((game, index) => (
              <div 
                key={game.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '15px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  color: '#FFC107'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <span style={{ 
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    minWidth: '30px',
                    color: index === 0 ? '#FFD700' : 
                           index === 1 ? '#C0C0C0' : 
                           index === 2 ? '#CD7F32' : '#FFC107'
                  }}>
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                  </span>
                  <div style={{ textAlign: 'left' }}>
                    {/* Show game name if present */}
                    {game.playerName && (
                      <div style={{ fontWeight: 'bold', color: '#fff', fontSize: '1.05rem', marginBottom: 2 }}>
                        {game.playerName}
                      </div>
                    )}
                    <div style={{ 
                      fontSize: '1rem',
                      marginBottom: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <span>👥</span> {game.totalAttempts} {game.totalAttempts === 1 ? 'person' : 'people'} tried
                    </div>
                    <div style={{ 
                      fontSize: '0.9rem',
                      opacity: 0.7,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <span>🎯</span> {game.successfulAttempts} {game.successfulAttempts === 1 ? 'person' : 'people'} succeeded
                    </div>
                  </div>
                </div>
                <a 
                  href={`/game/${game.id}`}
                  style={{
                    padding: '8px 20px',
                    backgroundColor: '#FFC107',
                    color: '#000',
                    borderRadius: '25px',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <span>🎮</span> Play
                </a>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            color: '#FFC107',
            fontSize: '1.2rem',
            textAlign: 'center',
            padding: '20px 0'
          }}>
            No community challenges found yet. Be the first to create one!
          </div>
        )}
      </div>

      <div style={{
        marginTop: '40px',
        fontSize: '0.9rem',
        color: '#aaa',
        zIndex: 5
      }}>
        <p>© {new Date().getFullYear()} games@Tashtaree. All rights reserved.</p>
      </div>

      {/* Create New Challenge Button (fixed at bottom right) */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 10
      }}>
        <button
          onClick={() => navigate('/game/create')}
          style={{
            padding: isScrolled ? '0' : '15px 40px',
            width: isScrolled ? '60px' : 'auto',
            height: isScrolled ? '60px' : 'auto',
            fontSize: isScrolled ? '2em' : '1.5rem',
            backgroundColor: '#E91E63',
            color: '#fff',
            border: 'none',
            borderRadius: isScrolled ? '50%' : '30px',
            cursor: 'pointer',
            boxShadow: '0 0 0 0 rgba(255, 193, 7, 0.7)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '8px',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation'
          }}
          className={`button-hover ${isScrolled ? 'always-pulse' : ''}`}
        >
          {isScrolled ? '🛸' : 'Create New Challenge 🛸'}
        </button>
        <div style={{
          color: '#FFC107',
          fontSize: '0.8rem',
          textAlign: 'center',
          opacity: 0.8,
          backgroundColor: '#000',
          padding: '4px 8px',
          borderRadius: '12px',
          maxWidth: isScrolled ? '80px' : '150px',
          transition: 'all 0.3s ease',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {isScrolled ? 'Create New' : 'For friends'}
        </div>
      </div>
    </div>
  );
}